import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { createUseStyles } from "react-jss";
import * as Mixins from "../../theme/Mixins";
import * as InputTokens from "../../theme/inputThemeTokens";
import type { Theme } from "../../types/Theme";

interface StyleProps {
    theme: Theme;
    /** Keyboard/tab focus halo on the outer shell (modern theme). */
    focused?: boolean;
    /** Pointer hover on the input shell. */
    hovered?: boolean;
    /** Caret present / DOM focus — drives primary border while editing. */
    editing?: boolean;
    /** Hover or edit — drives active input background. */
    borderHighlighted?: boolean;
    freeform?: boolean;
    accessoryLeft?: IconProp;
    accessoryRight?: IconProp;
    disableTextareaResize?: boolean;
}

function resolveTextInputBorderActive(editing?: boolean): boolean {
    return !!editing;
}

function getHeight(freeform: boolean, theme: Theme) {
    const { TextFontSize, BaseFontSize, InputHeight } = theme;
    if (freeform || Number(TextFontSize) > BaseFontSize) {
        return undefined;
    }
    // Via the token so a string/missing BorderWidth resolves the same way the border itself does.
    return InputHeight + InputTokens.resolveFieldBorderWidth(theme) * 2;
}

export default createUseStyles<string, StyleProps, Theme>({
    inputBox: {
        boxSizing: "border-box",
        color: ({ theme }) => Mixins.foregroundColor(theme, Mixins.controlBackgroundColor(theme)),
        composes: "input-box",
        height: ({ theme, freeform }) => getHeight(!!freeform, theme),
        lineHeight: ({ theme, freeform }) =>
            freeform ? `${theme.LineHeight}px` : `${theme.InputHeight}px`,
        overflow: ({ theme }) =>
            InputTokens.resolveTextInputFocusRingStroke(theme) > 0 ? "visible" : "hidden",
        position: "relative",
        transition: "color 200ms, box-shadow 200ms, border-color 200ms",
        verticalAlign: "top",
        width: "100%",
        "& [type=tel], & [type=number]": {
            textAlign: "right",
        },
        "& select, & input, & textarea, & button": {
            backgroundColor: ({ theme, borderHighlighted }) =>
                InputTokens.resolveTextInputBackground(theme, !!borderHighlighted),
            border: ({ theme, editing }) =>
                InputTokens.resolveTextInputBorder(theme, resolveTextInputBorderActive(editing)),
            borderRadius: ({ theme }) => InputTokens.resolveTextInputInnerBorderRadius(theme),
            // Must stay AFTER `border`: JSS applies function values in key order via CSSOM
            // setProperty, and the `border` shorthand resets every border-bottom longhand. Sorting
            // this line above `border` would silently drop the underline on re-render.
            borderBottom: ({ theme, editing }) =>
                InputTokens.resolveTextInputBorderBottom(
                    theme,
                    resolveTextInputBorderActive(editing),
                ),
            boxSizing: "border-box",
            color: "inherit",
            display: "block",
            fontFamily: "inherit",
            fontSize: "inherit",
            height: "100%",
            lineHeight: "inherit",
            outline: "none",
            overflow: "auto",
            padding: ({ theme }) => [
                [
                    InputTokens.resolveTextInputPaddingY(theme),
                    InputTokens.resolveTextInputPaddingX(theme),
                ],
            ],
            paddingLeft: ({ theme, accessoryLeft }) =>
                accessoryLeft
                    ? InputTokens.resolveTextInputPaddingInlineWithAccessory(theme)
                    : undefined,
            paddingRight: ({ theme, accessoryRight }) =>
                accessoryRight
                    ? InputTokens.resolveTextInputPaddingInlineWithAccessory(theme)
                    : undefined,
            resize: "none",
            // Logical start follows page dir (rtl → right) so OE/textarea caret matches text inputs
            textAlign: "start",
            transition: "background-color 200ms, border-color 200ms, box-shadow 200ms",
            WebkitAppearance: "none",
            width: "100%",
        },
        "& button": {
            padding: ({ theme }) => [[0, InputTokens.resolveTextInputPaddingX(theme)]],
        },
        "& textarea": {
            maxWidth: "100%",
            minWidth: "100%",
            resize: ({ disableTextareaResize }) => (disableTextareaResize ? "none" : "vertical"),
            minHeight: ({ theme }) => getHeight(false, theme),
        },
        "& option": {
            color: "black",
        },
    },
    inputBoxFocused: {
        composes: "input-box--focused",
        "&::after": {
            content: ({ theme }) =>
                InputTokens.resolveTextInputFocusRingStroke(theme) > 0 ? '""' : "none",
            display: ({ theme }) =>
                InputTokens.resolveTextInputFocusRingStroke(theme) > 0 ? "block" : "none",
            position: "absolute",
            top: ({ theme }) => -InputTokens.resolveTextInputFocusRingOffset(theme),
            right: ({ theme }) => -InputTokens.resolveTextInputFocusRingOffset(theme),
            bottom: ({ theme }) => -InputTokens.resolveTextInputFocusRingOffset(theme),
            left: ({ theme }) => -InputTokens.resolveTextInputFocusRingOffset(theme),
            borderRadius: ({ theme }) => InputTokens.resolveTextInputFocusRingBorderRadius(theme),
            border: ({ theme, editing }) =>
                `${InputTokens.resolveTextInputFocusRingStroke(theme)}px solid ${InputTokens.resolveTextInputFocusRingColor(
                    theme,
                    editing,
                )}`,
            pointerEvents: "none",
            boxSizing: "border-box",
        },
    },
    inputBoxAccessory: {
        bottom: 0,
        composes: "input-box__accessory",
        fontSize: ({ theme }) => theme.TextFontSize,
        margin: "auto",
        pointerEvents: "none",
        position: "absolute",
        top: 0,
    },
    inputBoxAccessoryLeft: {
        composes: "input-box__accessory-left",
        left: 0,
        marginLeft: ({ theme }) => InputTokens.resolveTextInputAccessoryMargin(theme),
    },
    inputBoxAccessoryRight: {
        composes: "input-box__accessory-right",
        marginRight: ({ theme }) => InputTokens.resolveTextInputAccessoryMargin(theme),
        right: 0,
    },
    inputBoxAccessoryRightInteractive: {
        cursor: "pointer",
        pointerEvents: "auto",
    },
    inputBoxDisabled: {
        composes: "input-box__disabled",
        opacity: 0.5,
    },
});
